import React, {useEffect, useState} from "react";
import Api from "../../../Api/api";
import {BeatLoader} from "react-spinners";
import {useForm} from "react-hook-form";
import {toast} from "react-toastify";

const AdminEditPotenzialParameter = () => {
    const [potenzialParameters, setPotenzialParameters] = useState([]);
    const [loading, setLoading] = useState(false);
    const {register, handleSubmit, formState: {errors}, reset} = useForm({mode: "onChange"});
    const [newParameter, setNewParameter] = useState(null);

    useEffect(() => {
        setLoading(true);
        Api().get("/getPotenzialParameters").then(res => {
            setPotenzialParameters(res.data);
            setLoading(false)
        }).catch(e => {
            setLoading(false)
            toast.error("Fehler beim Laden der Potenzial-Parameter.", toastConfig);
        });
    }, []);

    const onSubmit = async (data) => {
        setLoading(true);
        const cleanedData = Object.entries(data).reduce((acc, [key, value]) => {
            if (key.startsWith('bewertungssumme_')) {
                acc[key] = parseGermanNumber(value);
            } else {
                acc[key] = value;
            }
            return acc;
        }, {});

        data = cleanedData;

        try {
            // Handle new parameter if it exists
            if (newParameter) {
                const newParamPayload = {
                    produkt: data[`produkt_new`] || "",
                    bewertungssumme: data[`bewertungssumme_new`] || "",
                    maBeteiligung: data[`maBeteiligung_new`] || "",
                };

                await Api().post("/setPotenzialParameter", newParamPayload);
                toast.success("Neuer Potenzial-Parameter erfolgreich hinzugefügt.", toastConfig);
                setNewParameter(null); // Reset new param
            }

            // Update existing parameters
            const updatedParameters = potenzialParameters.map((param, index) => ({
                ...param,
                produkt: data[`produkt_${index}`],
                bewertungssumme: data[`bewertungssumme_${index}`],
                maBeteiligung: data[`maBeteiligung_${index}`],
            }));

            await Api().post("/updatePotenzialParameters", {parameters: updatedParameters});
            toast.success("Potenzial-Parameter erfolgreich aktualisiert.", toastConfig);

            // Reload parameters
            const res = await Api().get("/getPotenzialParameters");
            setPotenzialParameters(res.data);
        } catch (error) {
            console.error("Error:", error);
            toast.error("Fehler beim Speichern der Potenzial-Parameter.", toastConfig);
        } finally {
            setLoading(false);
        }
    };

    const parseGermanNumber = (value: string) => {
        if (!value) return 0;
        return parseFloat(value.replace(/\./g, '').replace(',', '.'));
    }

    const handleAddParameter = () => {
        setNewParameter({produkt: "", bewertungssumme: "", maBeteiligung: ""});
    };

    return (
        <div className='dashboardContainer'>
            <div className='flex justify-between items-center pb-5'>
                <h2 className='text-2xl'>Potenzial-Parameter</h2>
                <button
                    onClick={handleAddParameter}
                    className='px-6 py-2 float-right bg-mainBlue text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 '
                >
                    Parameter hinzufügen
                </button>
            </div>

            <div className='bg-white rounded-md mb-5 px-5 pt-4 pb-10 min-h-fit'>
                {loading ? (
                    <div className="flex justify-center">
                        <BeatLoader size={10} color="#3A46A9"/>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div className='grid grid-cols-12 gap-2'>
                            <div className='col-span-1 font-bold'>Produkt</div>
                            <div className='col-span-2 font-bold'>Bewertungssumme (BWS)</div>
                            <div className='col-span-2 font-bold'>MA-Beteiligung % (MA%)</div>
                            <div className='col-span-7'></div>

                            {potenzialParameters.map((param, index) => {
                                const formattedBewertungssumme = new Intl.NumberFormat('de-DE', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                }).format(param.bewertungssumme || 0);

                                return (
                                    <React.Fragment key={index}>
                                        <div className='col-span-1 px-2 py-1'>
                                            <input
                                                type='text'
                                                {...register(`produkt_${index}`, {required: true})}
                                                defaultValue={param.produkt}
                                                className='shadow appearance-none rounded w-full py-1 px-2 text-gray-700'
                                            />
                                            {errors[`produkt_${index}`] &&
                                                <p className='text-red-500 text-xs italic'>Pflichtfeld.</p>}
                                        </div>
                                        <div className='col-span-2 px-2 py-1'>
                                            <input
                                                type='text'
                                                {...register(`bewertungssumme_${index}`, {required: true})}
                                                defaultValue={formattedBewertungssumme}
                                                className='shadow appearance-none rounded w-full py-1 px-2 text-gray-700 leading-tight focus:outline-none focus:shadow-outline'
                                            />
                                            {errors[`bewertungssumme_${index}`] &&
                                                <p className='text-red-500 text-xs italic'>Bitte eingeben.</p>}
                                        </div>
                                        <div className='col-span-2 px-2 py-1'>
                                            <input
                                                type='number'
                                                {...register(`maBeteiligung_${index}`, {required: true})}
                                                defaultValue={param.maBeteiligung}
                                                className='shadow appearance-none rounded w-20 py-1 px-2 text-gray-700 leading-tight focus:outline-none focus:shadow-outline'
                                            />
                                            {errors[`maBeteiligung_${index}`] &&
                                                <p className='text-red-500 text-xs italic'>Bitte eingeben.</p>}
                                        </div>
                                        <div className='col-span-7'></div>
                                    </React.Fragment>
                                );
                            })}
                            {newParameter && (
                                <React.Fragment>
                                    <div className='col-span-1 px-2 py-1'>
                                        <input
                                            type='text'
                                            {...register(`produkt_new`, {required: true})}
                                            placeholder="Produktname"
                                            className='shadow appearance-none rounded w-full py-1 px-2 text-gray-700 leading-tight focus:outline-none focus:shadow-outline'
                                        />
                                        {errors[`produkt_new`] &&
                                            <p className='text-red-500 text-xs italic'>Produktname eingeben.</p>}
                                    </div>
                                    <div className='col-span-2 px-2 py-1'>
                                        <input
                                            type='text'
                                            {...register(`bewertungssumme_new`, {required: true})}
                                            placeholder="z. B. 10000"
                                            className='shadow appearance-none rounded w-full py-1 px-2 text-gray-700 leading-tight focus:outline-none focus:shadow-outline'
                                        />
                                        {errors[`bewertungssumme_new`] &&
                                            <p className='text-red-500 text-xs italic'>Bewertungssumme eingeben.</p>}
                                    </div>
                                    <div className='col-span-2 px-2 py-1'>
                                        <input
                                            type='number'
                                            {...register(`maBeteiligung_new`, {required: true})}
                                            placeholder="%"
                                            className='shadow appearance-none rounded w-20 py-1 px-2 text-gray-700 leading-tight focus:outline-none focus:shadow-outline'
                                        />
                                        {errors[`maBeteiligung_new`] &&
                                            <p className='text-red-500 text-xs italic'>MA-Beteiligung % eingeben.</p>}
                                    </div>
                                    <div className='col-span-7'></div>
                                </React.Fragment>
                            )}
                        </div>

                        <div className='grid grid-cols-12 gap-2 mt-4'>
                            <div className='col-span-12'>
                                <button
                                    type='submit'
                                    disabled={loading}
                                    className={`px-6 py-2 float-right bg-mainBlue text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${loading ? "opacity-50 cursor-not-allowed" : "hover:bg-[rgb(58,70,169)]"}`}
                                >
                                    {loading ? (<BeatLoader size={8} color="#ffffff"/>) : ("Speichern")}
                                </button>
                            </div>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default AdminEditPotenzialParameter;
